import type { CourseLessonDripRule } from "../api";

const MS_PER_DAY = 86_400_000;

function startOfUtcDayFromYmd(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  return new Date(Date.UTC(y, mo - 1, d));
}

export type DripAvailability = {
  blocked: boolean;
  unlockAt: Date | null;
  /** When blocked but unlock instant unknown (e.g. days-after without grantedAt) */
  scheduleUnknown: boolean;
};

/**
 * Best-effort client mirror of drip semantics for UX-only locks.
 * Server lesson access remains authoritative when drip is enforced API-side.
 */
export function resolveDripAvailability(
  dripRule: CourseLessonDripRule | undefined,
  now: Date,
  grantedAt: Date | null,
): DripAvailability {
  if (!dripRule || dripRule.type === "immediate") {
    return { blocked: false, unlockAt: null, scheduleUnknown: false };
  }

  if (dripRule.type === "specific_date") {
    const raw = dripRule.specificDate?.trim();
    if (!raw) return { blocked: false, unlockAt: null, scheduleUnknown: false };
    const unlockStart = startOfUtcDayFromYmd(raw);
    if (!unlockStart) return { blocked: false, unlockAt: null, scheduleUnknown: false };
    if (now.getTime() < unlockStart.getTime()) {
      return { blocked: true, unlockAt: unlockStart, scheduleUnknown: false };
    }
    return { blocked: false, unlockAt: unlockStart, scheduleUnknown: false };
  }

  if (dripRule.type === "days_after_enrollment") {
    const days = dripRule.daysAfterEnrollment ?? 0;
    if (!grantedAt) {
      return { blocked: true, unlockAt: null, scheduleUnknown: true };
    }
    const unlockAt = new Date(grantedAt.getTime() + days * MS_PER_DAY);
    if (now.getTime() < unlockAt.getTime()) {
      return { blocked: true, unlockAt, scheduleUnknown: false };
    }
    return { blocked: false, unlockAt, scheduleUnknown: false };
  }

  return { blocked: false, unlockAt: null, scheduleUnknown: false };
}
